import {
  ChangeEvent,
  useRef,
  useState,
  KeyboardEvent,
  FC,
  useEffect,
} from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux-hooks';
import { setChatData, setReplyText } from '../../store/chat.slice';
import useStorage from '../../hooks/useStorage';
import { v4 as uuidv4 } from 'uuid';
import { TChatMsg } from '../../types/chat.types';
import LogoDark from '../../assets/images/logo-dark-new.svg';
import LogoLight from '../../assets/images/logo-new.png';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { map } from 'lodash';

export const AISearchBox: FC<{ isCompareModel: boolean }> = ({
  isCompareModel,
}) => {
  const [userMsg, setUserMsg] = useState('');
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const { chatData, chatLoading, replyText } = useAppSelector((state) => state.chat);
  const { agentInfo, threadId, questions, textColor } = useAppSelector(
    (state) => state.common
  );
  const { getItem, setItem } = useStorage();
  const dispatch = useAppDispatch();
  const pasteAreaRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLDivElement | null>(null);
  const { width } = useWindowDimensions();
  const chatSuggestionsStyles =
  `flex items-center px-5 py-2 cursor-pointer rounded-full hover:bg-hoverBgColor`;

  const setUserChatData = () => {
    if (!userMsg.trim()) return;
    const msgId = uuidv4();

    dispatch(
      setChatData([
        ...chatData,
        {
          msgId,
          ques: userMsg,
          answer: '',
          model: agentInfo?.llmModel || '',
          chatId: threadId,
          assistantId: agentInfo?._id || '',
          replyText: replyText || undefined,
        },
      ])
    );
    const localChatData: TChatMsg[] = JSON.parse(getItem('chatData') || '[]');
    setItem(
      'chatData',
      JSON.stringify([
        ...localChatData,
        {
          msgId,
          ques: userMsg,
          answer: '',
          model: agentInfo?.llmModel || '',
          chatId: threadId,
          assistantId: agentInfo?._id || '',
          replyText: replyText || undefined,
        },
      ])
    );

    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
    setUserMsg('');
    dispatch(setReplyText(''));
  };

  const handleInputChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setUserMsg(event.target.value);
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      userMsg.trim().length !== 0 &&
      !chatLoading
    ) {
      event.preventDefault();
      setUserChatData();
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: any) => {
      // Don't interfere with copy/paste/cut or other system shortcuts
      if (event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }

      // Don't focus if user is selecting text
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        return;
      }

      const active = document.activeElement;

      // If active element is not an input, textarea, or contenteditable
      if (
        active &&
        !(active instanceof HTMLInputElement) &&
        !(active instanceof HTMLTextAreaElement) &&
        !(active instanceof HTMLElement && active.isContentEditable)
      ) {
        // textareaRef.current?.focus();
        const input = textareaRef.current?.querySelector('textarea');
        if (input) (input as HTMLTextAreaElement).focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div className='relative'>
      <div
        className={`
          flex items-center border justify-between shadow-md flex-wrap
          dark:border-primary/[0.6] rounded-2xl sm:rounded-[30px] dark:bg-[#303030] ai-search-box
          ${
            chatData.length > 0
              ? 'w-full border-b-0 sm:border-b-[1px]'
              : 'w-full lg:w-[700px] 2xl:w-[800px] mx-auto'
          }
        `}
        ref={pasteAreaRef}
        style={{border: `1px solid ${textColor}`}}
        tabIndex={0} // Makes the div focusable
      >
        {replyText && !isCompareModel && (
          <div
            className="w-full max-w-[85vw] sm:max-w-full relative flex overflow-x-auto py-2 mx-1 bg-[#f9f9f9] gap-3 px-4 items-start
              rounded-tr-[30px] rounded-tl-[30px] rounded-bl-[10px] dark:bg-[#303030] rounded-br-[10px] mt-1"
          >
            <i className="icon-enter text-[20px]" />
            <p className="text-[14px] text-[#5d5d5d] dark:text-[#bdbdbd] grow">{replyText}</p>
            <i
              className="icon-cross text-[20px] cursor-pointer"
              onClick={() => dispatch(setReplyText(''))}
            />
          </div>
        )}
        <div
          className="flex items-center grow flex-col rounded-2xl sm:rounded-[30px] px-4 py-3 dark:bg-[#303030]"
          ref={textareaRef}
        >
          <textarea
            className={`
              border-0 grow max-h-[140px] min-h-[35px] w-full cursor-text bg-transparent dark:text-white pl-2
              disabled:cursor-default focus:border-0 focus:outline-none my-2 resize-none text-[14px] sm:text-[16px]
            `}
            placeholder="Ask anything..."
            onChange={handleInputChange}
            value={userMsg ?? ''}
            rows={chatData.length > 0 ? 1 : 2}
            ref={inputRef}
            onKeyDown={handleKeyPress}
          />
          <div className="flex justify-end items-center w-full mt-2">
            <div className="flex items-center">
              <button
                className="
                  bg-[#0d0d0d] disabled:bg-[#0d0d0d]/[0.3] dark:bg-white h-[35px] w-[35px]
                  rounded-2xl sm:rounded-[30px] disabled:pointer-events-none text-white dark:text-[#0d0d0d]
                  dark:border dark:border-primary/[0.6] transition dark:disabled:bg-[#424242]
                "
                onClick={setUserChatData}
                disabled={chatLoading || !userMsg}
                type="button"
              >
                <i className="icon-arrow-up text-2xl block" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="relative w-full lg:w-[700px] 2xl:w-[800px] mx-auto">
        {!chatData.length && width > 500 && (
          <div className="flex items-center justify-center flex-wrap gap-4 dark:text-white overflow-x-auto mt-6">
            {map(questions, (item, index) => (
              <div
                className={chatSuggestionsStyles}
                style={{border: `1px solid ${textColor}`}}
                onClick={() => setUserMsg(item)}
                key={`${item}-${index}`}
              >
                <span className="text-[12px] sm:text-[14px]">{item}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        className={`justify-center items-center hidden sm:flex fixed left-0 w-full bottom-[15px]`}
      >
        <div className="flex items-center">
          <p className="text-[14px] sm:text-[16px] font-light">Powered by</p>
          <div className="flex items-center justify-center ml-2">
            <img
              src={LogoLight}
              alt="Answerr Logo"
              className="inline-block dark:hidden rotate-[85deg] sm:rotate-0 w-[22px] h-auto mr-1"
            />
            <img
              src={LogoDark}
              alt="Answerr Logo"
              className="hidden dark:inline-block rotate-[85deg] sm:rotate-0 w-[22px] h-auto mr-1"
            />
            <span className="text-[17px] sm:text-[20px] font-semibold mt-[-3px] dark:text-white hidden sm:block">
              answerr
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
